/**
 * Grid of game tiles used on the home page (and reusable elsewhere).
 *
 * Each tile shows the game name, tagline, and a live "N open lobbies"
 * counter. Selecting a tile is a single click — the parent decides what
 * happens next (the home page expands a create form below the grid).
 *
 * Adding a new game requires zero changes here: the picker reads from the
 * client-side `GAMES` registry.
 */

import { GAMES } from "../games/registry.js";
import type { GameKind } from "../../../shared/protocol.js";
import { GameKindBadge } from "./GameKindBadge.js";

export interface GameTilePickerProps {
  /** Currently selected kind. `null` = nothing selected (no highlight). */
  value: GameKind | null;
  onChange: (kind: GameKind) => void;
  /** Live counts of open public lobbies, keyed by GameKind. */
  lobbyCounts: Partial<Record<GameKind, number>>;
}

export function GameTilePicker({ value, onChange, lobbyCounts }: GameTilePickerProps) {
  return (
    <div className="game-tile-grid" role="radiogroup" aria-label="Pick a game">
      {GAMES.map((g) => {
        const count = lobbyCounts[g.kind] ?? 0;
        const selected = value === g.kind;
        return (
          <button
            key={g.kind}
            type="button"
            role="radio"
            aria-checked={selected}
            className={`game-tile${selected ? " selected" : ""}`}
            onClick={() => onChange(g.kind)}
          >
            <div className="game-tile-head">
              <GameKindBadge kind={g.kind} size={36} />
              <div className="game-tile-title">{g.label}</div>
            </div>
            <div className="game-tile-tagline">{g.tagline}</div>
            <div className="game-tile-foot">
              <span className={`game-tile-count${count > 0 ? " hot" : ""}`}>
                {count === 0
                  ? "no open lobbies"
                  : count === 1
                  ? "1 open lobby"
                  : `${count} open lobbies`}
              </span>
              <span className="game-tile-seats">
                {g.seatBounds.min === g.seatBounds.max
                  ? `${g.seatBounds.min} players`
                  : `${g.seatBounds.min}-${g.seatBounds.max} players`}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}
